import type { Cell, ColorId, Pos } from "./types.js";
import type { Rng } from "./rng.js";

export function cloneGrid(grid: Cell[][]): Cell[][] {
  return grid.map((row) => row.map((c) => (c ? { ...c } : null)));
}

export function inBounds(grid: Cell[][], r: number, c: number): boolean {
  return r >= 0 && c >= 0 && r < grid.length && c < (grid[0]?.length ?? 0);
}

export function cellAt(grid: Cell[][], r: number, c: number): Cell {
  return grid[r]?.[c] ?? null;
}

const DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

/** Connected same-color group containing (r,c), 4-neighborhood. Empty if not a color cell. */
export function findGroup(grid: Cell[][], r: number, c: number): Pos[] {
  const start = cellAt(grid, r, c);
  if (!start || start.kind !== "color") return [];
  const color = start.color;
  const h = grid.length;
  const w = grid[0]?.length ?? 0;
  const seen = new Uint8Array(h * w);
  const out: Pos[] = [];
  const stack: Pos[] = [{ r, c }];
  seen[r * w + c] = 1;
  while (stack.length) {
    const p = stack.pop()!;
    out.push(p);
    for (const [dr, dc] of DIRS) {
      const nr = p.r + dr;
      const nc = p.c + dc;
      if (!inBounds(grid, nr, nc)) continue;
      const idx = nr * w + nc;
      if (seen[idx]) continue;
      const cell = grid[nr]![nc];
      if (cell && cell.kind === "color" && cell.color === color) {
        seen[idx] = 1;
        stack.push({ r: nr, c: nc });
      }
    }
  }
  return out;
}

/** All groups of size >= minSize. Each group listed once (by its cells). */
export function findAllGroups(grid: Cell[][], minSize = 2): Pos[][] {
  const h = grid.length;
  const w = grid[0]?.length ?? 0;
  const seen = new Uint8Array(h * w);
  const groups: Pos[][] = [];
  for (let r = 0; r < h; r++) {
    for (let c = 0; c < w; c++) {
      if (seen[r * w + c]) continue;
      const cell = grid[r]![c];
      if (!cell || cell.kind !== "color") continue;
      const g = findGroup(grid, r, c);
      for (const p of g) seen[p.r * w + p.c] = 1;
      if (g.length >= minSize) groups.push(g);
    }
  }
  return groups;
}

export function hasAnyMove(grid: Cell[][]): boolean {
  for (const row of grid) {
    for (const cell of row) {
      if (cell && (cell.kind === "rocket" || cell.kind === "bomb")) return true;
    }
  }
  return findAllGroups(grid, 2).length > 0;
}

export function randomColorCell(rng: Rng, colors: number): Cell {
  return { kind: "color", color: rng.int(colors) as ColorId };
}

/**
 * Apply gravity column by column. Crates do NOT fall (they are fixed obstacles) — cells above a
 * crate stack on top of it. Returns list of falls (from->to) for animation.
 */
export function applyGravity(grid: Cell[][]): { from: Pos; to: Pos }[] {
  const h = grid.length;
  const w = grid[0]?.length ?? 0;
  const falls: { from: Pos; to: Pos }[] = [];
  for (let c = 0; c < w; c++) {
    // Process segments separated by crates.
    let segBottom = h - 1;
    while (segBottom >= 0) {
      // find segment [segTop..segBottom] with no crate
      let segTop = segBottom;
      while (segTop >= 0 && grid[segTop]![c]?.kind !== "crate") segTop--;
      // segTop is crate index or -1; segment is segTop+1..segBottom
      let write = segBottom;
      for (let r = segBottom; r > segTop; r--) {
        const cell = grid[r]![c];
        if (cell) {
          if (write !== r) {
            grid[write]![c] = cell;
            grid[r]![c] = null;
            falls.push({ from: { r, c }, to: { r: write, c } });
          }
          write--;
        }
      }
      segBottom = segTop - 1;
    }
  }
  return falls;
}

/** Fill empty cells with random colors, spawning from the top of each segment. */
export function refill(grid: Cell[][], rng: Rng, colors: number): { pos: Pos; cell: Cell }[] {
  const h = grid.length;
  const w = grid[0]?.length ?? 0;
  const spawned: { pos: Pos; cell: Cell }[] = [];
  // Deterministic order: column-major, top to bottom, so replay matches.
  for (let c = 0; c < w; c++) {
    for (let r = 0; r < h; r++) {
      if (grid[r]![c] === null) {
        const cell = randomColorCell(rng, colors);
        grid[r]![c] = cell;
        spawned.push({ pos: { r, c }, cell });
      }
    }
  }
  return spawned;
}

/** Deterministic reshuffle of color cells only (specials and crates stay). */
export function shuffleColors(grid: Cell[][], rng: Rng): void {
  const positions: Pos[] = [];
  const cells: Cell[] = [];
  for (let r = 0; r < grid.length; r++) {
    for (let c = 0; c < (grid[0]?.length ?? 0); c++) {
      const cell = grid[r]![c];
      if (cell && cell.kind === "color") {
        positions.push({ r, c });
        cells.push(cell);
      }
    }
  }
  // Fisher-Yates with our rng
  for (let i = cells.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    const tmp = cells[i]!;
    cells[i] = cells[j]!;
    cells[j] = tmp;
  }
  positions.forEach((p, i) => {
    grid[p.r]![p.c] = cells[i]!;
  });
}

export function gridToString(grid: Cell[][]): string {
  return grid
    .map((row) =>
      row
        .map((c) => {
          if (!c) return ".";
          if (c.kind === "color") return String(c.color);
          if (c.kind === "rocket") return c.dir === "h" ? "-" : "|";
          if (c.kind === "bomb") return "*";
          return c.hp > 1 ? "Y" : "X";
        })
        .join(""),
    )
    .join("\n");
}
